<template>
  <div class="tfa-block">
    <div v-if="step === 'notice'" class="tfa-block__notice">
      <div class="tfa-block__alert tfa-block__alert--info" role="status">
        <span class="tfa-block__alert-icon">
          <svg viewBox="0 0 20 21" fill="none" xmlns="http://www.w3.org/2000/svg">
            <path
              fill-rule="evenodd"
              clip-rule="evenodd"
              d="M10.0001 19.5221C5.02522 19.5221 0.977905 15.4746 0.977905 10.4999C0.977905 5.52509 5.02522 1.47788 10.0001 1.47788C14.975 1.47788 19.0221 5.52509 19.0221 10.4999C19.0221 15.4746 14.975 19.5221 10.0001 19.5221ZM10.0001 0.5C4.48618 0.5 0 4.98603 0 10.4999C0 16.0139 4.48618 20.5 10.0001 20.5C15.5141 20.5 20 16.0139 20 10.4999C20 4.98603 15.5141 0.5 10.0001 0.5Z"
              fill="#1f1f1f"
            />
            <path
              fill-rule="evenodd"
              clip-rule="evenodd"
              d="M10 8.77588C9.80967 8.77588 9.65518 8.99577 9.65518 9.2667V15.1816C9.65518 15.4525 9.80967 15.6724 10 15.6724C10.1904 15.6724 10.3448 15.4525 10.3448 15.1816V9.2667C10.3448 8.99577 10.1905 8.77588 10 8.77588Z"
              fill="#1f1f1f"
            />
            <path
              fill-rule="evenodd"
              clip-rule="evenodd"
              d="M10.0023 6.70689C9.80747 6.70689 9.65517 6.55579 9.65517 6.36281C9.65517 6.16911 9.80786 6.01723 10.0023 6.01723C10.191 6.01723 10.3448 6.1722 10.3448 6.36281C10.3448 6.55231 10.191 6.70689 10.0023 6.70689ZM10.002 5.32758C9.4302 5.32758 8.96552 5.79193 8.96552 6.3627C8.96552 6.93286 9.4302 7.39654 10.002 7.39654C10.5711 7.39654 11.0345 6.93286 11.0345 6.3627C11.0345 5.79193 10.5711 5.32758 10.002 5.32758Z"
              fill="#1f1f1f"
            />
          </svg>
        </span>
        <div class="tfa-block__alert-content">
          <div class="tfa-block__alert-title">Protecting your account is our highest priority</div>
          <div class="tfa-block__alert-body">For your account security, we now require two-factor authentication</div>
        </div>
      </div>

      <p class="tfa-block__copy">
        From now on every time you login to your account, we'll send a verification code to
        <em v-if="destinationMasked">{{ destinationMasked }}</em>
      </p>

      <ul class="tfa-block__list">
        <li>
          {{ isSmsChannel ? 'Codes are sent instantly to your phone' : 'Codes are sent instantly to your inbox' }}
        </li>
        <li>Each code is valid for 15 minutes</li>
        <li>You'll only need to verify once per session</li>
      </ul>

      <div class="tfa-block__btn-row tfa-block__btn-row--start">
        <cg-button class="tfa-block__btn tfa-block__btn--compact" @click="goToVerify">
          Next
        </cg-button>
      </div>
    </div>

    <div v-else-if="step === 'verify'" class="tfa-block__verify">
      <h2 class="tfa-block__title">Two-step authentication</h2>

      <div class="tfa-block__alert" :class="{ 'tfa-block__alert--phone': isSmsChannel }" role="status">
        <span class="tfa-block__alert-icon">
          <!-- Phone glyph for the SMS channel, envelope for email. -->
          <svg
            v-if="isSmsChannel"
            viewBox="0 0 24 24"
            fill="none"
            width="24"
            height="24"
            xmlns="http://www.w3.org/2000/svg"
            aria-hidden="true"
          >
            <rect x="7" y="2" width="10" height="20" rx="2" stroke="#1f1f1f" stroke-width="1.5" />
            <path d="M10 18.5H14" stroke="#1f1f1f" stroke-width="1.5" stroke-linecap="round" />
          </svg>
          <svg
            v-else
            viewBox="0 0 24 24"
            fill="none"
            width="22"
            height="22"
            xmlns="http://www.w3.org/2000/svg"
            aria-hidden="true"
          >
            <rect x="3" y="5" width="18" height="14" rx="1.5" stroke="#1f1f1f" stroke-width="1.6" />
            <path
              d="M3.5 6.5L12 13L20.5 6.5"
              stroke="#1f1f1f"
              stroke-width="1.6"
              stroke-linecap="round"
              stroke-linejoin="round"
            />
          </svg>
        </span>
        <div class="tfa-block__alert-content">
          <div class="tfa-block__alert-body">
            <template v-if="!isExpired">
              <span>
                {{
                  isSmsChannel
                    ? 'A text message with your code has been sent to'
                    : 'An email with your code has been sent to'
                }}
                <b v-if="destinationMasked">{{ destinationMasked }}</b>.
              </span>
              <span v-if="expiryCountdown > 0" class="tfa-block__alert-expiry">
                Code will be valid for {{ expiryMinutesLabel }} min
              </span>
            </template>
            <template v-else>
              <span class="tfa-block__alert-expired">Code expired.</span>
            </template>
          </div>
        </div>
      </div>

      <div v-if="showUndeliveredBanner" class="tfa-block__alert tfa-block__alert--undelivered" role="alert">
        <div class="tfa-block__alert-content">
          <div class="tfa-block__alert-title">We couldn't deliver the text message to {{ destinationMasked }}</div>
          <div class="tfa-block__alert-body">
            {{
              fallbackUnavailable
                ? 'Use "Resend code" to try again.'
                : 'Use "Resend code" to try again, or get the code by email instead.'
            }}
          </div>
        </div>
      </div>

      <p class="tfa-block__prompt">To continue please enter your 6 digit verification code:</p>

      <form @submit.prevent="onVerify">
        <div class="tfa-block__code-row">
          <div
            class="tfa-block__code-input"
            :class="{
              'tfa-block__code-input--error': !!verifyError,
              'tfa-block__code-input--success': verifySuccess,
            }"
          >
            <input
              v-for="(_, idx) in 6"
              :key="idx"
              ref="codeInputs"
              v-model="digits[idx]"
              type="text"
              inputmode="numeric"
              autocomplete="one-time-code"
              maxlength="1"
              :aria-label="`Digit ${idx + 1}`"
              :disabled="inputsDisabled"
              @input="onDigitInput($event, idx)"
              @keydown="onDigitKeydown($event, idx)"
              @paste="onDigitPaste"
              @focus="onDigitFocus"
            />
          </div>

          <div class="tfa-block__resend">
            <template v-if="resendsExhausted">
              <span class="tfa-block__resend-exhausted">No resends left</span>
            </template>
            <template v-else-if="resendCountdown > 0">
              <button type="button" class="tfa-block__resend-link" disabled>Resend code</button>
              <span class="tfa-block__resend-timer">in {{ resendCountdownLabel }}</span>
            </template>
            <template v-else>
              <button
                type="button"
                class="tfa-block__resend-link"
                :disabled="resendLoading || isExpired || tooManyAttempts"
                @click="onResend"
              >
                Resend code
              </button>
            </template>

            <template v-if="isSmsChannel && !fallbackUnavailable">
              <span class="tfa-block__resend-divider" aria-hidden="true"></span>
              <button
                type="button"
                class="tfa-block__resend-link"
                :disabled="fallbackDisabled"
                @click="onEmailFallback"
              >
                {{ fallbackLoading ? 'Sending…' : 'Send code by email instead' }}
              </button>
            </template>
          </div>

          <div v-if="fallbackError" class="tfa-block__code-error">
            <span class="tfa-block__code-error-icon">i</span>
            <span>{{ fallbackError }}</span>
          </div>

          <div v-if="verifyError" class="tfa-block__code-error">
            <span class="tfa-block__code-error-icon">i</span>
            <span>{{ verifyError }}</span>
          </div>

          <div v-if="verifySuccess" class="tfa-block__code-success">
            <span class="tfa-block__code-success-icon">&#10003;</span>
            <span>Code verified &mdash; signing you in...</span>
          </div>
        </div>

        <div v-if="isExpired" class="tfa-block__btn-row">
          <cg-button block @click="$emit('back-to-login')">
            Back to login
          </cg-button>
        </div>
      </form>
    </div>
  </div>
</template>

<script>
import { verify2FAAction, resend2FAAction, sms2FAStatusAction, email2FAFallbackAction } from '@/services/authService'
import { CgButton } from '@corporategiftcom/design-system'
import { clockOffsetFrom } from '@/utils/twoFactor'

export default {
  name: 'TwoFactorAuthBlock',
  components: {
    CgButton,
  },
  props: {
    email: {
      type: String,
      required: true,
    },
    challenge: {
      type: Object,
      required: true,
    },
    initialStep: {
      type: String,
      default: 'notice',
    },
  },
  data () {
    return {
      step: this.initialStep,
      digits: ['', '', '', '', '', ''],
      challengeRef: { ...this.challenge },
      verifyError: '',
      verifySuccess: false,
      verifyLoading: false,
      tooManyAttempts: false,
      serverExpired: false,
      resendLoading: false,
      resendsExhausted: false,
      now: Date.now(),
      tickInterval: null,
      initialResendCleared: false,
      smsStatus: null,
      smsPollTimer: null,
      smsPollDeadline: 0,
      smsPollGeneration: 0,
      smsPollIntervalMs: 10000,
      fallbackLoading: false,
      fallbackUsed: false,
      fallbackUnavailable: false,
      fallbackError: '',
    }
  },
  computed: {
    emailMasked () {
      return this.challengeRef.email_masked || ''
    },
    isSmsChannel () {
      return this.challengeRef.channel === 'sms'
    },
    // The masked value the code was actually delivered to. `phone_masked` is only
    // populated on the SMS channel, so fall back to the email when it is missing.
    destinationMasked () {
      if (!this.isSmsChannel) { return this.emailMasked }

      const phone = this.challengeRef.phone_masked

      return phone ? this.formatPhoneMasked(phone) : this.emailMasked
    },
    expiresAtMs () {
      return new Date(this.challengeRef.expires_at).getTime()
    },
    resendAvailableAtMs () {
      return new Date(this.challengeRef.resend_available_at).getTime()
    },
    expiryCountdown () {
      return Math.max(0, Math.ceil((this.expiresAtMs - this.now) / 1000))
    },
    resendCountdown () {
      return Math.max(0, Math.ceil((this.resendAvailableAtMs - this.now) / 1000))
    },
    clockSynced () {
      return typeof this.challengeRef.clock_offset_ms === 'number'
    },
    // A device clock that's off (wrong time zone, manually set time) would make
    // a fresh code look expired, so the local countdown only decides once it's
    // corrected by the server time. Otherwise the backend's CHALLENGE_NOT_FOUND
    // is the only thing that expires the challenge.
    isExpired () {
      return this.serverExpired || (this.clockSynced && this.expiryCountdown <= 0)
    },
    isCodeComplete () {
      return this.digits.every((d) => /^\d$/.test(d))
    },
    inputsDisabled () {
      return this.isExpired || this.verifyLoading || this.verifySuccess || this.tooManyAttempts
    },
    expiryMinutesLabel () {
      return Math.max(1, Math.ceil(this.expiryCountdown / 60))
    },
    resendCountdownLabel () {
      return `${this.resendCountdown}s`
    },
    showUndeliveredBanner () {
      return this.smsStatus === 'undelivered' && !this.fallbackUsed && !this.isExpired
    },
    fallbackDisabled () {
      return this.fallbackLoading || this.tooManyAttempts
    },
  },
  watch: {
    challenge: {
      handler (val) {
        this.challengeRef = { ...val }
      },
      deep: true,
    },
    step: {
      immediate: true,
      handler (value) {
        if (value === 'verify') {
          if (!this.initialResendCleared) {
            this.initialResendCleared = true
            this.updateChallenge({
              ...this.challengeRef,
              resend_available_at: new Date(this.serverNow()).toISOString(),
            })
          }
          this.now = this.serverNow()
          this.startTicker()
          this.startSmsStatusPolling()
          this.$nextTick(() => {
            this.$refs.codeInputs?.[0]?.focus()
          })
        } else {
          this.stopTicker()
          this.stopSmsStatusPolling()
        }
      },
    },
  },
  beforeDestroy () {
    this.stopTicker()
    this.stopSmsStatusPolling()
  },
  methods: {
    /**
     * Design uses asterisks for the masked middle. Keep any visible prefix/suffix
     * from the API (e.g. `+4••••••••10` → `+4********10`).
     */
    formatPhoneMasked (masked) {
      const value = String(masked).trim()

      if (!value) { return '' }

      return value.replace(/[•·●*]+/g, (match) => '*'.repeat(match.length))
    },
    updateChallenge (updated) {
      this.challengeRef = updated
      this.$emit('update:challenge', updated)
    },
    serverNow () {
      return Date.now() + (this.challengeRef.clock_offset_ms ?? 0)
    },
    // Keeps the previous offset when a response carries no `server_time`.
    clockOffsetFor (payload) {
      return clockOffsetFrom(payload?.server_time) ?? this.challengeRef.clock_offset_ms ?? null
    },
    markExpired () {
      this.stopSmsStatusPolling()
      this.serverExpired = true
    },
    startTicker () {
      this.stopTicker()
      this.tickInterval = setInterval(() => {
        this.now = this.serverNow()
      }, 1000)
    },
    stopTicker () {
      if (this.tickInterval) {
        clearInterval(this.tickInterval)
        this.tickInterval = null
      }
    },
    startSmsStatusPolling () {
      this.stopSmsStatusPolling()

      if (!this.isSmsChannel || this.fallbackUsed) { return }

      this.smsStatus = null
      this.fallbackUnavailable = false
      this.fallbackError = ''
      this.smsPollIntervalMs = 10000
      this.smsPollDeadline = Date.now() + 60000
      this.scheduleNextSmsPoll(this.smsPollGeneration)
    },
    scheduleNextSmsPoll (generation) {
      if (generation !== this.smsPollGeneration) { return }

      const remaining = this.smsPollDeadline - Date.now()

      if (remaining <= 0) { return }

      this.smsPollTimer = setTimeout(
        () => this.pollSmsStatus(generation),
        Math.min(this.smsPollIntervalMs, remaining),
      )
    },
    async pollSmsStatus (generation) {
      try {
        const data = await sms2FAStatusAction({
          challenge_token: this.challengeRef.challenge_token,
        })

        if (generation !== this.smsPollGeneration) { return }

        if (data?.email_fallback_used) {
          this.fallbackUsed = true
          this.stopSmsStatusPolling()
          this.updateChallenge({ ...this.challengeRef, channel: 'email' })
          return
        }

        if (data?.sms_status === 'undelivered' || data?.sms_status === 'delivered') {
          this.smsStatus = data.sms_status
          this.stopSmsStatusPolling()
          return
        }

        this.smsStatus = data?.sms_status ?? this.smsStatus
      } catch (error) {
        if (generation !== this.smsPollGeneration) { return }

        const errData = error?.response?.data ?? error ?? {}
        const errCode = errData?.error_code || errData?.code

        if (errData?.status === 401 || errCode === 'CHALLENGE_NOT_FOUND') {
          this.markExpired()
          return
        }

        if (errData?.status === 429 || /too many/i.test(errData?.message || '')) {
          this.smsPollIntervalMs *= 2
        }
      }

      this.scheduleNextSmsPoll(generation)
    },
    // Bumping the generation invalidates any poll already in flight: its
    // continuation neither touches state nor re-arms a timer, so a teardown
    // or a restarted window can never be overwritten by a stale response.
    stopSmsStatusPolling () {
      this.smsPollGeneration += 1

      if (this.smsPollTimer) {
        clearTimeout(this.smsPollTimer)
        this.smsPollTimer = null
      }
    },
    async onEmailFallback () {
      if (this.fallbackDisabled || this.isExpired) { return }

      this.fallbackLoading = true
      this.fallbackError = ''

      try {
        const payload = await email2FAFallbackAction({
          challenge_token: this.challengeRef.challenge_token,
        })

        this.fallbackUsed = true
        this.stopSmsStatusPolling()

        this.updateChallenge({
          ...this.challengeRef,
          channel: 'email',
          resend_available_at: payload?.resend_available_at ?? this.challengeRef.resend_available_at,
          expires_at: payload?.challenge_expires_at ?? this.challengeRef.expires_at,
          clock_offset_ms: this.clockOffsetFor(payload),
          resends_left: payload?.resends_left,
        })

        if (typeof payload?.resends_left === 'number' && payload.resends_left <= 0) {
          this.resendsExhausted = true
        }
      } catch (error) {
        const errData = error?.response?.data ?? error ?? {}
        const errCode = errData?.error_code || errData?.code

        if (errCode === 'CHALLENGE_NOT_FOUND') {
          this.markExpired()
          return
        }

        if (errCode === 'FALLBACK_NOT_APPLICABLE' || errCode === 'SMS_DELIVERY_NOT_FAILED') {
          this.fallbackUnavailable = true
          return
        }

        if (errCode === 'RESEND_COOLDOWN' && errData?.available_at) {
          this.updateChallenge({
            ...this.challengeRef,
            resend_available_at: errData.available_at,
          })
          return
        }

        if (errCode === 'RESEND_LIMIT_EXCEEDED') {
          this.resendsExhausted = true
          return
        }

        this.fallbackError = errData?.message || 'Could not send the email. Please try again.'
      } finally {
        this.fallbackLoading = false
      }
    },
    goToVerify () {
      this.step = 'verify'
    },
    onDigitInput (event, idx) {
      const target = event.target
      const raw = target.value.replace(/\D/g, '')

      if (raw.length === 0) {
        this.$set(this.digits, idx, '')
        return
      }

      if (raw.length > 1) {
        this.spreadCodeStartingAt(raw, idx)
        return
      }

      this.$set(this.digits, idx, raw)
      this.verifyError = ''

      if (idx < 5) {
        this.$refs.codeInputs?.[idx + 1]?.focus()
      } else if (this.isCodeComplete) {
        this.onVerify()
      }
    },
    onDigitKeydown (event, idx) {
      if (event.key === 'Backspace' && !this.digits[idx] && idx > 0) {
        this.$refs.codeInputs?.[idx - 1]?.focus()
        this.$set(this.digits, idx - 1, '')
        event.preventDefault()
      } else if (event.key === 'ArrowLeft' && idx > 0) {
        this.$refs.codeInputs?.[idx - 1]?.focus()
        event.preventDefault()
      } else if (event.key === 'ArrowRight' && idx < 5) {
        this.$refs.codeInputs?.[idx + 1]?.focus()
        event.preventDefault()
      }
    },
    onDigitPaste (event) {
      const text = event.clipboardData?.getData('text') ?? ''
      const digitsOnly = text.replace(/\D/g, '')

      if (!digitsOnly) { return }

      event.preventDefault()
      this.spreadCodeStartingAt(digitsOnly, 0)
    },
    onDigitFocus (event) {
      event.target.select()
    },
    spreadCodeStartingAt (raw, startIdx) {
      const chars = raw.slice(0, 6 - startIdx).split('')

      chars.forEach((char, index) => {
        this.$set(this.digits, startIdx + index, char)
      })

      this.verifyError = ''
      const nextIdx = Math.min(startIdx + chars.length, 5)

      this.$nextTick(() => {
        this.$refs.codeInputs?.[nextIdx]?.focus()

        if (this.isCodeComplete) {
          this.onVerify()
        }
      })
    },
    focusFirstDigit () {
      this.$nextTick(() => {
        this.$refs.codeInputs?.[0]?.focus()
      })
    },
    async onVerify () {
      if (!this.isCodeComplete || this.verifyLoading || this.isExpired) { return }

      this.verifyError = ''
      this.verifyLoading = true

      const code = this.digits.join('')

      try {
        const payload = await verify2FAAction({
          challenge_token: this.challengeRef.challenge_token,
          code,
        })

        this.verifyLoading = false
        this.verifySuccess = true
        this.stopSmsStatusPolling()
        setTimeout(() => this.$emit('success', payload), 1000)
      } catch (error) {
        this.verifyLoading = false
        const errData = error?.response?.data ?? error ?? {}
        const errCode = errData?.error_code || errData?.code
        const errMessage = errData?.message

        if (errCode === 'CHALLENGE_NOT_FOUND') {
          this.markExpired()
          this.verifyError = ''
          return
        }

        // ThrottleRequests, HTTP 429) only return { message: "Too Many Attempts." }.
        const tooManyMessage = /too many attempts/i.test(errMessage || '')

        if (errCode === 'TOO_MANY_ATTEMPTS' || error?.response?.status === 429 || tooManyMessage) {
          this.tooManyAttempts = true
          this.stopTicker()
          // No locked state — bounce straight back to the login block.
          this.$emit('back-to-login')
          return
        }

        this.verifyError = errMessage || 'The code is incorrect or has already expired, try again or click Resend code'
        this.focusFirstDigit()
      }
    },
    async onResend () {
      if (this.resendLoading || this.isExpired || this.resendsExhausted || this.tooManyAttempts) { return }

      this.resendLoading = true
      this.verifyError = ''

      try {
        const payload = await resend2FAAction({
          challenge_token: this.challengeRef.challenge_token,
        })

        const updated = {
          ...this.challengeRef,
          resend_available_at: payload?.resend_available_at ?? this.challengeRef.resend_available_at,
          expires_at: payload?.challenge_expires_at ?? this.challengeRef.expires_at,
          clock_offset_ms: this.clockOffsetFor(payload),
          resends_left: payload?.resends_left,
        }

        this.updateChallenge(updated)
        this.startSmsStatusPolling()

        if (typeof payload?.resends_left === 'number' && payload.resends_left <= 0) {
          this.resendsExhausted = true
        }
      } catch (error) {
        const errData = error?.response?.data ?? error ?? {}
        const errCode = errData?.error_code || errData?.code

        if (errCode === 'CHALLENGE_NOT_FOUND') {
          this.markExpired()
          return
        }

        // The local countdown can't be trusted on a skewed clock, so the
        // backend's cooldown is the one that counts.
        if (errCode === 'RESEND_COOLDOWN' && errData?.available_at) {
          this.updateChallenge({
            ...this.challengeRef,
            resend_available_at: errData.available_at,
          })
          return
        }

        if (errCode === 'RESEND_LIMIT_EXCEEDED') {
          this.resendsExhausted = true
          return
        }

        this.verifyError = errData?.message || 'Could not resend code. Please try again.'
      } finally {
        this.resendLoading = false
      }
    },
  },
}
</script>

<style lang="scss" scoped>
.tfa-block {
  font-family: 'Lato', sans-serif;
  color: #1a1a1a;

  &__title {
    font-weight: 300;
    font-size: 22px;
    line-height: 1.2;
    color: #000;
    margin: 0 0 24px 0;
  }

  &__alert {
    display: flex;
    gap: 16px;
    padding: 16px 18px;
    background: #f5f5f5;
    border-radius: 4px;
    align-items: center;

    &--info {
      background: #f6ecb8;
    }

    &--phone {
      .tfa-block__alert-icon {
        width: 24px;
        height: 24px;

        svg {
          width: 24px;
          height: 24px;
        }
      }

      .tfa-block__alert-body {
        display: flex;
        flex-direction: column;
        gap: 0;
      }
    }

    &--undelivered {
      background: #fbe7e9;
      margin-top: 12px;
      align-items: flex-start;
    }
  }

  &__alert-icon {
    flex-shrink: 0;
    width: 20px;
    height: 20px;
    display: inline-flex;
    align-items: center;
    justify-content: center;

    svg {
      width: 20px;
      height: 20px;
      display: block;
    }
  }

  &__alert-content {
    display: flex;
    flex-direction: column;
    gap: 4px;
    min-width: 0;
  }

  &__alert-title {
    font-weight: 700;
    font-size: 15px;
    line-height: 1.3;
    color: #000;
  }

  &__alert-body {
    font-weight: 400;
    font-size: 14px;
    line-height: 1.45;
    color: #1a1a1a;

    b {
      font-weight: 700;
    }
  }

  &__alert-expiry {
    display: block;
  }

  &__alert-expired {
    color: #b00020;
    font-weight: 700;
  }

  &__copy {
    margin-top: 28px;
    font-weight: 400;
    font-size: 15px;
    line-height: 1.55;
    color: #1a1a1a;

    em {
      font-style: italic;
      font-weight: 700;
    }
  }

  &__list {
    margin: 20px 0 0;
    padding: 0;
    list-style: none;
    display: flex;
    flex-direction: column;
    gap: 10px;

    li {
      font-weight: 400;
      font-size: 14px;
      line-height: 1.45;
      color: #1a1a1a;
      display: flex;
      gap: 10px;
      align-items: flex-start;

      &::before {
        content: '';
        flex-shrink: 0;
        width: 6px;
        height: 6px;
        margin-top: 8px;
        border-radius: 50%;
        background: #000;
      }
    }
  }

  &__prompt {
    margin-top: 28px;
    font-weight: 400;
    font-size: 16px;
    line-height: 1.4;
    color: #1a1a1a;
  }

  &__code-row {
    margin-top: 18px;
    display: flex;
    flex-direction: column;
    gap: 14px;
  }

  &__code-input {
    display: flex;
    gap: 10px;

    input {
      width: 44px;
      height: 56px;
      border: 1px solid #d3d2d2;
      border-radius: 8px;
      background: #fff;
      text-align: center;
      font-weight: 700;
      font-size: 22px;
      line-height: 1;
      color: #000;
      outline: none;
      transition:
        background 0.18s cubic-bezier(0.4, 0, 0.2, 1),
        border-color 0.18s cubic-bezier(0.4, 0, 0.2, 1);
      font-family: 'Lato', sans-serif;

      &:focus {
        border-color: #b0a0ff;
        background: #f6f5ff;
      }

      &:disabled {
        background: #f5f5f5;
        color: #a1a1a1;
        cursor: not-allowed;
      }
    }

    &--error input,
    &--error input:disabled {
      border-color: #ff5a60;
      background: #fbe7e9;
      color: #000;
      -webkit-text-fill-color: #000;
    }

    &--success input,
    &--success input:disabled {
      border-color: #42b77a;
      background: #d8f1e4;
      color: #000;
      -webkit-text-fill-color: #000;
      cursor: default;
    }
  }

  &__code-error {
    display: flex;
    gap: 10px;
    align-items: center;
    font-style: italic;
    font-weight: 400;
    font-size: 14px;
    line-height: 1.4;
    color: #ff5a60;
    letter-spacing: 0.186667px;
    padding: 12px 14px;
    background: #fbe7e9;
    border-radius: 4px;
  }

  &__code-error-icon {
    flex-shrink: 0;
    width: 20px;
    height: 20px;
    border-radius: 50%;
    background: #ff5a60;
    color: #fff;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    font-weight: 700;
    font-size: 13px;
    line-height: 1;
    font-style: normal;
    margin-top: 1px;
  }

  &__code-success {
    display: flex;
    gap: 10px;
    align-items: center;
    font-weight: 400;
    font-size: 14px;
    line-height: 1.4;
    color: #219358;
    padding: 12px 14px;
    background: #d8f1e4;
    border-radius: 4px;
  }

  &__code-success-icon {
    flex-shrink: 0;
    width: 20px;
    height: 20px;
    border-radius: 50%;
    background: #42b77a;
    color: #fff;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    font-weight: 700;
    font-size: 12px;
    line-height: 1;
  }

  &__resend {
    display: flex;
    gap: 6px;
    align-items: center;
  }

  &__resend-link {
    appearance: none;
    background: transparent;
    border: 0;
    padding: 0;
    font-style: italic;
    font-weight: 700;
    font-size: 14px;
    line-height: 1.4;
    color: #6e50ff;
    cursor: pointer;

    &:hover:not(:disabled) {
      color: #5a3fd9;
    }

    &:disabled {
      color: #95979d;
      cursor: not-allowed;
    }
  }

  &__resend-timer {
    font-style: italic;
    font-weight: 400;
    font-size: 14px;
    line-height: 1.4;
    color: #95979d;
  }

  &__resend-exhausted {
    color: #b00020;
    font-style: italic;
    font-size: 14px;
  }

  &__resend-divider {
    width: 1px;
    align-self: stretch;
    background: #d3d2d2;
    margin: 0 4px;
  }

  &__btn-row {
    margin-top: 28px;

    &--start {
      display: flex;
      justify-content: flex-start;
    }
  }

  &__btn {
    min-width: 92px;
  }

  @media screen and (max-width: 430px) {
    &__title {
      font-size: 20px;
    }

    &__code-input {
      gap: 6px;

      input {
        width: 100%;
        flex: 1 1 0;
        min-width: 0;
        height: 48px;
        font-size: 20px;
      }
    }
  }
}
</style>
